// A card of Ultime news, connected: when it opens in place it asks the item's
// detail (GET /items/{origin}/{item_id}) and hands NewsCard the text as
// neutral paragraphs, the notices and the state of the call. NewsCard never
// sees the api; closed, the card asks nothing.
import { useMemo } from "react";
import { Link, useLocation } from "react-router";

import { NewsCard, type NewsCardDetail, type NewsCardProps } from "../../components/news/NewsCard";
import type { NewsItem } from "../../data/mock";
import { pageLinkState, type PageLinkState } from "../../lib/navigation";
import { parseItemId, toItemBody, type Origin } from "./adapt";
import { useItem } from "./api";

export const READ_MORE = "Leggi altro";
export const INCOMPLETE = "Testo non completo: apri l’originale";

/**
 * «Leggi altro» under a preview: the card's own page, with the rest of the
 * text and the files (the detail is in the cache under the same key: the
 * page asks nothing again); the link's look is the «Indietro» of the source
 * view (SourcesPanel).
 */
export function ReadMore({ news, state }: { news: NewsItem; state: PageLinkState }) {
  return (
    <Link to={news.pageUrl} state={state} className="self-start rounded-sm text-xs leading-5 font-medium text-accent">
      {READ_MORE}
    </Link>
  );
}

export function ExpandedNewsCard(props: NewsCardProps) {
  const ref = parseItemId(props.news.id);
  // every card of the lists has an id from itemId: one that does not parse cannot be asked; the row of a
  // sitting (its id is the api's `sitting:…`, never opened in place) has nothing to ask either
  if (!ref) return <NewsCard {...props} detail={{ status: "error" }} />;
  return <ConnectedNewsCard {...props} origin={ref.origin} itemId={ref.id} />;
}

function ConnectedNewsCard({ origin, itemId, ...props }: NewsCardProps & { origin: Origin; itemId: string }) {
  const open = props.expanded && props.chevron !== "right";
  const query = useItem(origin, itemId, open);
  const location = useLocation();
  const content = useMemo(() => (query.data ? toItemBody(query.data) : null), [query.data]);

  let detail: NewsCardDetail;
  if (content) {
    const state = pageLinkState(location);
    detail = {
      status: "ready",
      // the one notice: «incomplete», the text is partial
      notices: content.notices.map(() => INCOMPLETE),
      more: content.more ? <ReadMore news={props.news} state={state} /> : undefined,
    };
  }
  // a failed call being retried («Riprova») is loading again, not still failed
  else if (query.isError && !query.isFetching) detail = { status: "error", onRetry: () => void query.refetch() };
  else detail = { status: "loading" };

  return <NewsCard {...props} news={{ ...props.news, body: content?.body ?? [] }} detail={detail} />;
}
